
import React, { useRef, useEffect, useState } from 'react';
import { Canvas, FabricImage, PencilBrush } from 'fabric';
import { Trash2, Hand, RotateCcw, Brush, Palette } from 'lucide-react';

interface RedesignCanvasProps {
  imageUrl: string;
  onMaskGenerated: (maskData: any) => void;
}

type DrawMode = 'red' | 'green' | 'none';

export const RedesignCanvas: React.FC<RedesignCanvasProps> = ({ imageUrl, onMaskGenerated }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<Canvas | null>(null);
  const [mode, setMode] = useState<DrawMode>('none');
  const [brushSize, setBrushSize] = useState(20);
  const historyRef = useRef<any[]>([]);
  const scaleFactorRef = useRef<number>(1);
  const alignedImageRef = useRef<string | null>(null); // Store the aligned base image
  
  const onMaskGeneratedRef = useRef(onMaskGenerated);
  useEffect(() => {
    onMaskGeneratedRef.current = onMaskGenerated;
  }, [onMaskGenerated]);

  const emitMasks = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const objects = canvas.getObjects();
      const originalBg = canvas.backgroundImage;
      const originalBgColor = canvas.backgroundColor;
      const multiplier = scaleFactorRef.current;

      // Helper to generate a mask for a specific color (White on Black)
      const generateSingleMask = (targetColor: string) => {
          // Hide background
          canvas.backgroundImage = undefined;
          canvas.backgroundColor = '#000000'; // Black background

          let hasObjects = false;

          objects.forEach((obj: any) => {
              // Check if this object was drawn with the target color
              const isTarget = obj.originalColor === targetColor;
              
              if (isTarget) {
                  obj.visible = true;
                  obj.stroke = '#ffffff'; // Make it white for the mask
                  obj.opacity = 1;
                  hasObjects = true;
              } else {
                  obj.visible = false; // Hide other colors
              }
          });

          if (!hasObjects) {
              return null;
          }

          canvas.renderAll();
          return canvas.toDataURL({ format: 'png', multiplier });
      };

      // 1. Generate Red Mask (Binary)
      const redMask = generateSingleMask('rgba(217, 48, 37, 0.6)');

      // 2. Generate Green Mask (Binary)
      const greenMask = generateSingleMask('rgba(34, 197, 94, 0.6)');

      // 3. Generate Combined Overlay (Transparent BG, Colored Strokes)
      canvas.backgroundImage = undefined;
      canvas.backgroundColor = 'transparent';
      
      objects.forEach((obj: any) => {
          obj.visible = true;
          obj.opacity = 1;
          if (obj.originalColor) obj.stroke = obj.originalColor; // Restore original color
      });
      canvas.renderAll();
      const combinedMask = canvas.toDataURL({ format: 'png', multiplier });

      // 4. RESTORE CANVAS STATE & Generate Composite (Base Image + Strokes)
      canvas.backgroundImage = originalBg;
      canvas.backgroundColor = originalBgColor;
      objects.forEach((obj: any) => {
          obj.visible = true;
          if (obj.originalColor) obj.stroke = obj.originalColor;
      });
      canvas.renderAll();
      
      // Capture the visible state (Image + Drawings)
      const compositeImage = canvas.toDataURL({ format: 'png', multiplier });

      onMaskGeneratedRef.current({
          redMask,
          greenMask,
          combinedMask, 
          compositeImage, // Base Image with strokes burned in
          alignedImage: alignedImageRef.current 
      });
  };

  useEffect(() => {
    if (!imageUrl || !containerRef.current) return;
    
    historyRef.current = [];
    alignedImageRef.current = null;
    onMaskGeneratedRef.current({ redMask: null, greenMask: null, combinedMask: null, compositeImage: null, alignedImage: null });

    if (canvasRef.current) {
      canvasRef.current.dispose();
      canvasRef.current = null;
    }

    containerRef.current.innerHTML = '';
    const canvasEl = document.createElement('canvas');
    containerRef.current.appendChild(canvasEl);

    const initCanvas = async () => {
        const canvas = new Canvas(canvasEl, {
            isDrawingMode: false,
            backgroundColor: 'transparent'
        });
        
        canvasRef.current = canvas;

        try {
            const fImg = await FabricImage.fromURL(imageUrl, { crossOrigin: 'anonymous' });
            
            const maxWidth = 800;
            const originalWidth = fImg.width || 1;
            const scale = Math.min(1, maxWidth / originalWidth);
            scaleFactorRef.current = 1 / scale;

            const width = (fImg.width || 0) * scale;
            const height = (fImg.height || 0) * scale;

            canvas.setDimensions({ width, height });
            fImg.scaleToWidth(width);
            canvas.backgroundImage = fImg;
            canvas.renderAll();

            // CAPTURE ALIGNED IMAGE
            // We export the canvas background immediately to get a base image 
            // that perfectly aligns with the coordinate system of future masks.
            alignedImageRef.current = canvas.toDataURL({ format: 'png', multiplier: scaleFactorRef.current });
            
            // Initial emit to set alignedImage in parent
            onMaskGeneratedRef.current({ 
                redMask: null, 
                greenMask: null, 
                combinedMask: null,
                compositeImage: alignedImageRef.current,
                alignedImage: alignedImageRef.current 
            });

            const brush = new PencilBrush(canvas);
            brush.width = 20;
            canvas.freeDrawingBrush = brush;

            canvas.on('path:created', (e: any) => {
                const path = e.path;
                path.originalColor = canvas.freeDrawingBrush?.color;
                historyRef.current.push(path);
                emitMasks();
            });

        } catch (err) {
            console.error("Error loading fabric image:", err);
        }
    };

    initCanvas();

    return () => {
      if (canvasRef.current) {
        canvasRef.current.dispose();
        canvasRef.current = null;
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (mode === 'none') {
      canvas.isDrawingMode = false;
    } else {
      canvas.isDrawingMode = true;
      if (canvas.freeDrawingBrush) {
        canvas.freeDrawingBrush.width = brushSize;
        if (mode === 'red') {
            canvas.freeDrawingBrush.color = 'rgba(217, 48, 37, 0.6)';
        } else if (mode === 'green') {
            canvas.freeDrawingBrush.color = 'rgba(34, 197, 94, 0.6)';
        }
      }
    }
  }, [mode, brushSize]);

  const undoLast = () => {
    const canvas = canvasRef.current;
    if (!canvas || historyRef.current.length === 0) return;
    const lastPath = historyRef.current.pop();
    if (lastPath) {
      canvas.remove(lastPath);
      emitMasks();
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    [...historyRef.current].forEach(obj => canvas.remove(obj));
    historyRef.current = [];
    emitMasks();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 items-center justify-between">
        <div className="flex gap-2">
          <button 
            onClick={() => setMode('red')} 
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium border transition-all ${mode === 'red' ? 'bg-red-100 border-red-500 text-red-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
          >
            <Brush size={14} className={mode === 'red' ? 'text-red-600' : 'text-slate-400'} /> Red Mask
          </button>
          
          <button 
            onClick={() => setMode('green')} 
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium border transition-all ${mode === 'green' ? 'bg-green-100 border-green-500 text-green-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
          >
            <Brush size={14} className={mode === 'green' ? 'text-green-600' : 'text-slate-400'} /> Green Mask
          </button>

          <button 
            onClick={() => setMode('none')} 
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium border transition-all ${mode === 'none' ? 'bg-slate-200 border-slate-400 text-slate-700' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
          >
            <Hand size={16} /> Pan
          </button>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-500">Size:<input type="range" min="5" max="100" value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} className="w-24 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-600" /><span className="w-8 text-center font-mono">{brushSize}</span></label>
          <div className="h-6 w-px bg-slate-300 mx-1"></div>
          <button onClick={undoLast} className="p-1.5 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors" title="Undo"><RotateCcw size={18} /></button>
          <button onClick={clearCanvas} className="p-1.5 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Clear All"><Trash2 size={18} /></button>
        </div>
      </div>
      <div className="relative w-full flex justify-center overflow-hidden border rounded-lg bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-slate-100">
        <div ref={containerRef} className="max-w-full h-auto shadow-sm" />
      </div>
      <p className="text-xs text-slate-500 italic text-center">Use Red and Green brushes to select different areas for different instructions.</p>
    </div>
  );
};
